import {
  ActionIcon,
  Box,
  Flex,
  Tabs,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SignaturePad from "signature_pad";
import { FiEdit } from "react-icons/fi";
import { TbKeyboard } from "react-icons/tb";
import { CiImageOn } from "react-icons/ci";
import { IoIosCloseCircle } from "react-icons/io";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import { encrypt } from "../../utils";

const useStyles = createStyles(() => ({
  wrapper: {
    width: "100%",
    fontFamily: "Inter",
  },
  tabList: {
    background: "#EAECF0",
    borderRadius: "8px 8px 0px 0px",
    border: "none",
    justifyContent: "center",
  },
  tab: {
    color: "#979797 !important",
    fontFamily: "Inter",
    fontSize: 14,
    marginBottom: 0,
    padding: "12px 16px",
    fontWeight: 500,
    borderWidth: "2px !important",
    "& > span": {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    "&:hover": {
      color: "#27272A !important",
      background: "transparent",
    },
    "&[data-active]": {
      color: "#27272A !important",
      borderColor: "#27272A",
      "&:hover": {
        borderColor: "#27272A",
      },
    },
  },
  padParent: {
    position: "relative",
    width: "100%",
    display: "block",
    "& canvas": {
      display: "block",
      width: "100%",
      border: "0.5px solid #CBD5E1",
      background: "#fff",
      cursor: "crosshair",
      touchAction: "none",
    },
  },
  textPad: {
    height: 160,
    backgroundColor: "#FFF",
    border: "0.5px solid #CBD5E1",
    padding: 10,
    "& input": {
      fontFamily: "'Island Moments', cursive",
      fontSize: 56,
      height: "unset",
      fontWeight: 400,
      color: "#344054",
      textAlign: "center",
    },
  },
  uploadBox: {
    height: 160,
    width: "100%",
    border: "0.5px solid #CBD5E1",
    backgroundColor: "#FFF",
    padding: "0px",
    "& .uploadBoxInner": {
      height: "100%",
      width: "100%",
    },
  },
  previewBox: {
    padding: 10,
    minHeight: 160,
    width: "100%",
    border: "0.5px solid #CBD5E1",
    backgroundColor: "#FFF",
  },
  previewImg: {
    display: "block",
    objectFit: "contain",
    maxHeight: 140,
    width: "auto",
    margin: "auto",
  },
  selectedImgClose: {
    position: "absolute",
    top: 6,
    right: 6,
    borderRadius: "50%",
    backgroundColor: "#27272A",
    paddingLeft: 1,
    paddingBottom: 0.5,
  },
}));

/**
 * Inline Draw / Type / Image signature capture.
 *
 * Nothing is uploaded or persisted while the user draws — the parent calls
 * `resolve()` (via ref) at save time, which uploads to S3 and returns the
 * value to store. That keeps the signature part of a single save action
 * instead of firing its own request.
 */
const SignaturePadField = forwardRef(({ height = 160 }, ref) => {
  const { classes } = useStyles();
  const [mode, setMode] = useState("draw");
  const [textValue, setTextValue] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  // The canvas needs an explicit pixel size — CSS scaling alone would blur the
  // strokes and offset the pointer, so size the backing store to the rendered
  // width and re-init when the box resizes.
  useEffect(() => {
    let cancelled = false;
    const setup = () => {
      const canvas = canvasRef.current;
      if (cancelled) return;
      if (!canvas) {
        setTimeout(setup, 50);
        return;
      }
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = canvas.offsetWidth || 600;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.height = `${height}px`;
      canvas.getContext("2d").scale(ratio, ratio);
      padRef.current?.off();
      padRef.current = new SignaturePad(canvas, {
        backgroundColor: "rgba(255,255,255,0)",
        penColor: "rgb(0,0,0)",
      });
    };
    setup();
    return () => {
      cancelled = true;
      padRef.current?.off();
    };
  }, [mode, height]);

  const isEmpty = () => {
    if (mode === "type") return !textValue.trim();
    if (mode === "image") return !selectedImage;
    return !padRef.current || padRef.current.isEmpty();
  };

  const clear = () => {
    padRef.current?.clear();
    setTextValue("");
    setSelectedImage(null);
  };

  const uploadAsSignature = async (file) => {
    const result = await uploadFileToS3Api({ file });
    return {
      signatureText: null,
      signatureImage: encrypt(result?.uploadURL),
    };
  };

  useImperativeHandle(ref, () => ({
    isEmpty,
    clear,
    /**
     * Uploads (when needed) and returns { signatureText, signatureImage },
     * or null when nothing has been entered.
     */
    resolve: async () => {
      if (isEmpty()) return null;

      if (mode === "type") {
        return { signatureText: textValue.trim(), signatureImage: null };
      }

      if (mode === "image") {
        return uploadAsSignature(selectedImage);
      }

      const dataUrl = padRef.current.toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      return uploadAsSignature(
        new File([blob], `signature_${Date.now()}.png`, { type: "image/png" })
      );
    },
  }));

  const handleTabChange = (value) => {
    // Only one signature is kept, so switching input methods starts clean.
    clear();
    setMode(value);
  };

  return (
    <Box className={classes.wrapper}>
      <Tabs value={mode} onTabChange={handleTabChange}>
        <Tabs.List className={classes.tabList}>
          <Tabs.Tab className={classes.tab} value="draw">
            <FiEdit size={18} /> Draw
          </Tabs.Tab>
          <Tabs.Tab className={classes.tab} value="type">
            <TbKeyboard size={20} /> Type
          </Tabs.Tab>
          <Tabs.Tab className={classes.tab} value="image">
            <CiImageOn size={20} /> Image
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="draw">
          <Box className={classes.padParent}>
            <canvas ref={canvasRef} />
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value="type">
          <Flex align="center" justify="center" className={classes.textPad}>
            <TextInput
              variant="unstyled"
              w="100%"
              placeholder="Type your name"
              value={textValue}
              onChange={(event) => setTextValue(event.currentTarget.value)}
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="image">
          {selectedImage ? (
            <Flex align="center" justify="center" className={classes.previewBox}>
              <Box pos="relative" display="inline-block">
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="Signature preview"
                  className={classes.previewImg}
                />
                <ActionIcon
                  variant="transparent"
                  className={classes.selectedImgClose}
                  onClick={() => setSelectedImage(null)}
                >
                  <IoIosCloseCircle size={24} color="#FFF" />
                </ActionIcon>
              </Box>
            </Flex>
          ) : (
            <Dropzone
              accept={IMAGE_MIME_TYPE}
              onDrop={(files) => setSelectedImage(files[0])}
              maxSize={2 * 1024 ** 2}
              multiple={false}
              classNames={{ root: classes.uploadBox, inner: "uploadBoxInner" }}
            >
              <Flex direction="column" align="center" justify="center" h="100%">
                <Text fz={15} fw={600} color="#171717" ff="Inter">
                  <Text span td="underline" inherit>
                    Click to Upload
                  </Text>{" "}
                  or Drag and Drop
                </Text>
                <Text fz={12} fw={500} c="#979797" ff="Inter" mt={6}>
                  Maximum File Size 2MB
                </Text>
              </Flex>
            </Dropzone>
          )}
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
});

export default SignaturePadField;
